import { Injectable } from '@angular/core';
import { FUNCIONALIDADES_SETOR_CODES } from '../../../@core/auth/funcionalidades-setor';
import { OperadoraModulo, UsuarioFuncionalidade } from '@gestor-web/@core/api/ords/model';
import { OperadorasSelectors, UserProfileSelectors } from '@gestor-web/@core/store/selectors';
import { MODULE_DISPLAY_NAMES, ModuleKey } from '@gestor-web/app/shared/config/feature-access.config';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, filter, map, Observable, of } from 'rxjs';

/** Funcionalidades de "Dados do Setor" (dados públicos da ANS): acessíveis a qualquer autenticado. */
const FUNCIONALIDADES_SETOR_SET = new Set<string>(FUNCIONALIDADES_SETOR_CODES.map(code => code.toUpperCase()));

/**
 * Estado de EXIBIÇÃO de uma funcionalidade:
 * - `acessivel`: o usuário consegue abrir a tela (usado pelos guards);
 * - `visivel`: o item aparece no menu/card;
 * - `locked`: o item aparece com cadeado ("a operadora não contratou").
 *
 * Administrador acessa tudo, mas continua vendo `locked` naquilo que a operadora do
 * workspace não contratou.
 */
export interface FuncionalidadeAcesso {
  acessivel: boolean;
  visivel: boolean;
  locked: boolean;
  lockedModules: string[];
}

const ACESSO_LIBERADO: FuncionalidadeAcesso = { acessivel: true, visivel: true, locked: false, lockedModules: [] };
const ACESSO_OCULTO: FuncionalidadeAcesso = { acessivel: false, visivel: false, locked: false, lockedModules: [] };

/**
 * O que `resolverAcesso` precisa para decidir. `funcionalidades` vem do endpoint de
 * funcionalidades; `isAdmin`, da `ib_administrador` do papel no perfil (`selectIsAdmin`), e cobre
 * Administrador e Super Administrador; `modulosOperadora` vem do store
 * (`selectWorkspaceModulosAtivos`), com os códigos em MINÚSCULO.
 */
export interface ContextoAcesso {
  funcionalidades: Map<string, UsuarioFuncionalidade>;
  isAdmin: boolean;
  modulosOperadora: Set<string>;
}

/**
 * Consulta as permissões de funcionalidade do usuário corrente. O back-end resolve a regra
 * (administrador, papel de acesso e módulo/operadora) em
 * `/geral/usuario/operadora/{regans}/funcionalidades`, já armazenada no store global: uma
 * funcionalidade é acessível quando está na lista E com `ib_desabilitado_aplicavel === 0`.
 *
 * A decisão de EXIBIÇÃO — esconder ou mostrar com cadeado — fica em `resolverAcesso`.
 */
@Injectable({
  providedIn: 'root',
})
export class PermissaoService {
  private modulos$: Observable<OperadoraModulo[]>;
  private modulosAtivos$: Observable<string[]>;
  private isAdmin$: Observable<boolean>;

  constructor(private store: Store) {
    this.modulos$ = this.store.select(OperadorasSelectors.selectWorkspaceModulos);
    this.modulosAtivos$ = this.store.select(OperadorasSelectors.selectWorkspaceModulosAtivos);
    this.isAdmin$ = this.store.select(UserProfileSelectors.selectIsAdmin);
  }

  /** Setorial ("Dados do Setor"): sempre acessível, fora da lista escopada por operadora. */
  isFuncionalidadeSetorial(feature: string): boolean {
    return !!feature && FUNCIONALIDADES_SETOR_SET.has(feature.toUpperCase().trim());
  }

  private hasAcessoFuncionalidade(code: string, acessiveis: Set<string>): boolean {
    if (!code) {
      return false;
    }
    return acessiveis.has(code.toUpperCase().trim());
  }

  /**
   * Acesso à feature informada; emite apenas após o carregamento da lista, e só quando o valor
   * muda: recarregar a lista (troca de operadora) não reemite, e a tela não reconstrói o grid.
   */
  checkAcessoFeature(feature: string): Observable<boolean> {
    if (!feature) {
      throw Error('Feature não informada');
    }

    // Liberamos as setoriais sem aguardar `funcionalidades_loaded`: a rota de "Dados do Setor"
    // não passa pelo `OperadoraWorkspaceGuard` que carrega a lista, então um deep link aberto
    // antes de haver operadora no workspace (ex.: logo após o login) travaria em tela branca.
    if (this.isFuncionalidadeSetorial(feature)) {
      return of(true);
    }

    return this.gatedAcessiveis$().pipe(
      map(acessiveis => this.hasAcessoFuncionalidade(feature, acessiveis)),
      distinctUntilChanged(),
    );
  }

  /** Mapa `{ feature: temAcesso }` para as features informadas. */
  checkAcessoFeaturesMap<K extends string>(...features: readonly K[]): Observable<Record<K, boolean>> {
    if (!features.length) {
      return of({} as Record<K, boolean>);
    }

    return this.gatedAcessiveis$().pipe(
      map(acessiveis => {
        const hasAcessoMap = {} as Record<K, boolean>;
        for (const feature of features) {
          if (!feature) {
            throw Error('Feature não informada');
          }
          hasAcessoMap[feature] = this.hasAcessoFuncionalidade(feature, acessiveis);
        }
        return hasAcessoMap;
      }),
    );
  }

  /** Acesso a pelo menos uma das features informadas. */
  checkAcessoAnyFeatures(...features: string[]): Observable<boolean> {
    return this.gatedAcessiveis$().pipe(
      map(acessiveis => features.some(feature => this.hasAcessoFuncionalidade(feature, acessiveis))),
    );
  }

  /**
   * Acesso por MÓDULO da operadora, não pela lista de funcionalidades. Só para os casos que
   * ainda não têm `cd_funcionalidade` no endpoint (ex.: NIPS, abas exclusivas Premium/Qualifica).
   * Administrador tem acesso irrestrito.
   */
  checkAcessoAnyModulos(...modulos: string[]): Observable<boolean> {
    const alvo = modulos.map(m => m.toLowerCase().trim());
    return combineLatest([this.isAdmin$, this.modulosAtivos$]).pipe(
      map(([isAdmin, modulosAtivos]) => {
        if (isAdmin) {
          return true;
        }
        const ativos = new Set((modulosAtivos ?? []).map(m => m.toLowerCase().trim()));
        return alvo.some(m => ativos.has(m));
      }),
    );
  }

  /** Equipe interna (`selectIsAdmin`), para itens visíveis apenas a administradores. */
  checkIsAdmin(): Observable<boolean> {
    return this.isAdmin$;
  }

  /**
   * Resolve o estado de exibição (visível / com cadeado) de uma funcionalidade — regra única,
   * aplicada por todas as telas e pelo menu:
   *
   * | Situação                                     | Não-administrador   | Administrador       |
   * |----------------------------------------------|---------------------|---------------------|
   * | não vendida por módulo, ou setorial          | conforme o acesso   | conforme o acesso   |
   * | contratada (tem módulo que habilita)         | acessa              | acessa              |
   * | teaser (tem o módulo, mas desabilitada nele) | cadeado, sem acesso | cadeado, com acesso |
   * | não contratada (não tem módulo que a ofereça)| não aparece         | cadeado, com acesso |
   *
   * Cada linha tem um `it` correspondente em `permissao.service.spec.ts`.
   *
   * "Ter o módulo" é decidido pelos módulos da operadora que vêm do store, não por
   * `modulos_liberados`: o administrador não tem vínculo em `apl_usuario_ops` e receberia esse
   * campo vazio, o que o faria ver cadeado em tudo.
   */
  resolverAcesso(feature: string, contexto: ContextoAcesso): FuncionalidadeAcesso {
    if (!feature) {
      return ACESSO_LIBERADO;
    }

    const code = feature.toUpperCase().trim();

    if (this.isFuncionalidadeSetorial(code)) {
      return ACESSO_LIBERADO;
    }

    const funcionalidade = contexto.funcionalidades.get(code);
    const acessivel = funcionalidade?.ib_desabilitado_aplicavel === 0;

    const disponiveis = funcionalidade?.modulos_disponiveis ?? [];

    // Não é vendida por módulo (ex.: `ADMIN.USUARIOS`, liberada por papel de acesso): segue só
    // o acesso e nunca recebe cadeado.
    if (!disponiveis.length) {
      return acessivel ? ACESSO_LIBERADO : ACESSO_OCULTO;
    }

    // Contratada: a operadora tem algum módulo que HABILITA a funcionalidade.
    const habilitados = funcionalidade?.modulos_habilitados ?? [];
    const contratada = this.operadoraTemAlgum(habilitados, contexto.modulosOperadora);

    if (contratada) {
      return acessivel ? ACESSO_LIBERADO : ACESSO_OCULTO;
    }

    // Teaser: a operadora tem o módulo, mas nele a funcionalidade está desabilitada. Sem nem
    // teaser, só o administrador enxerga o cadeado — e mesmo assim navega.
    const teaser = this.operadoraTemAlgum(disponiveis, contexto.modulosOperadora);

    if (teaser || contexto.isAdmin) {
      // O tooltip lista os módulos que DESTRAVAM a tela, não todos em que ela existe.
      return { acessivel, visivel: true, locked: true, lockedModules: this.toModuleDisplayNames(habilitados) };
    }

    // Não contratada. Para o usuário comum, `acessivel` aqui só pode ter vindo do papel de
    // acesso (ex.: Operador Coordenador com `ADMIN.USUARIOS` em operadora sem módulo vigente):
    // segue visível e sem cadeado; do contrário, some do menu.
    return acessivel ? ACESSO_LIBERADO : ACESSO_OCULTO;
  }

  /** Indica se a operadora do workspace possui ao menos um dos módulos informados. */
  private operadoraTemAlgum(modulos: string[], modulosOperadora: Set<string>): boolean {
    return modulos.some(modulo => modulosOperadora.has((modulo ?? '').toLowerCase().trim()));
  }

  /** Versão observable de `resolverAcesso`, para uso em templates com `async`. */
  checkExibicaoFeature(feature: string): Observable<FuncionalidadeAcesso> {
    return this.gatedContexto$().pipe(map(contexto => this.resolverAcesso(feature, contexto)));
  }

  /** Contexto de decisão para menus, cards e abas; emite apenas após o carregamento da lista. */
  getContextoAcesso(): Observable<ContextoAcesso> {
    return this.gatedContexto$();
  }

  /** 'PREMIUM' -> 'Premium' para o tooltip de bloqueio; código não mapeado é exibido como veio. */
  private toModuleDisplayNames(modulos: string[]): string[] {
    return modulos.map(modulo => MODULE_DISPLAY_NAMES[modulo.trim().toLowerCase() as ModuleKey] ?? modulo);
  }

  private gatedContexto$(): Observable<ContextoAcesso> {
    return combineLatest([
      this.store.select(UserProfileSelectors.selectFuncionalidadesLoaded),
      this.store.select(UserProfileSelectors.selectFuncionalidadesMap),
      this.isAdmin$,
      this.modulosAtivos$,
    ]).pipe(
      filter(([loaded]) => loaded),
      map(([, funcionalidades, isAdmin, modulosAtivos]) => ({
        funcionalidades,
        isAdmin,
        modulosOperadora: new Set((modulosAtivos ?? []).map(modulo => (modulo ?? '').toLowerCase().trim())),
      })),
    );
  }

  /** Módulos (com vigência) da operadora do workspace. Apenas para EXIBIÇÃO. */
  getModulosWorkspace() {
    return this.modulos$;
  }

  /** Chaves dos módulos ativos da operadora do workspace. Apenas para EXIBIÇÃO. */
  getModulosAtivos() {
    return this.modulosAtivos$;
  }

  /** Emite só após o load, evitando "piscar" itens liberados/bloqueados na primeira renderização. */
  private gatedAcessiveis$(): Observable<Set<string>> {
    return combineLatest([
      this.store.select(UserProfileSelectors.selectFuncionalidadesLoaded),
      this.store.select(UserProfileSelectors.selectFuncionalidadesAcessiveis),
    ]).pipe(
      filter(([loaded]) => loaded),
      map(([, acessiveis]) => acessiveis),
    );
  }
}
